// imports
import React from 'react';
import { NoSsr } from '@withjoy/joykit';
import { useLocation } from '@react-router';

// local imports
import { useEventInfo } from '@shared/utils/eventInfo';
import { useSetEventHandle } from '@shared/utils/eventInfo';
import { JoyLogoLoader } from '@shared/components/JoyLogoLoader';
import { EXCLUDED_EVENT_HANDLES } from '@shared/constants/excludedEventHandles';
import { useGetEventByIdForReservationStatusPageQuery } from '@graphql/generated';

/**
 * Props for the EventHandleFetcher component
 */
interface EventHandleFetcherProps {
  readonly children: (eventHandle: string) => React.ReactNode;
}

/**
 * Props for the memoized child component
 */
interface MemoizedChildrenProps
  extends Readonly<{
    eventHandle: string;
    children: React.ReactNode;
  }> {}

/**
 * Memoized component that only re-renders when eventHandle changes
 * This prevents unnecessary re-renders of child components when the parent re-renders
 * but the eventHandle remains the same.
 */
const MemoizedChildrenForEventHandle = React.memo<MemoizedChildrenProps>(({ eventHandle, children }: MemoizedChildrenProps) => {
  useSetEventHandle(EXCLUDED_EVENT_HANDLES.has(eventHandle) ? undefined : eventHandle);
  const eventInfo = useEventInfo();
  return eventInfo.loading ? <>Loading...</> : <>{children}</>;
});

// Set display name for debugging purposes
MemoizedChildrenForEventHandle.displayName = 'MemoizedChildrenForEventHandle';

/**
 * EventHandleFetcher component that fetches event data and provides the event handle to children
 *
 * This component extracts an eventId from URL search parameters, fetches the corresponding
 * event data using GraphQL, and renders children with the event's website handle when available.
 *
 * @param props - The component props
 * @param props.children - Render prop function that receives the event handle string
 * @returns JSX element or null if no eventId is present
 *
 * @example
 * ```tsx
 * <EventHandleFetcher>
 *   {(eventHandle) => <div>Event Handle: {eventHandle}</div>}
 * </EventHandleFetcher>
 * ```
 */
export const EventHandleFetcher = ({ children }: Readonly<EventHandleFetcherProps>) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const eventId = searchParams.get('eventId');

  const { data, loading, error } = useGetEventByIdForReservationStatusPageQuery({
    ssr: false,
    skip: !eventId,
    batchMode: 'fast',
    fetchPolicy: 'cache-and-network',
    variables: { eventId: eventId! },
  });

  if (!eventId) {
    return null;
  }

  const eventHandle = data?.eventById?.website;

  return (
    <NoSsr>
      {loading && <JoyLogoLoader loaderKey="event-handle-fetcher" />}
      {!loading && !error && eventHandle && <MemoizedChildrenForEventHandle eventHandle={eventHandle}>{children(eventHandle)}</MemoizedChildrenForEventHandle>}
    </NoSsr>
  );
};
